import styled from "@emotion/styled";
import {DragDropContext, Droppable, Draggable, OnDragEndResponder} from "react-beautiful-dnd";
import {DragIcon} from "../../../../common/icons";
import {COLORS} from "../../../../../constants/constants";
import {Text, Tooltip} from "@blueprintjs/core";
import {STOP_INFORMATION_OPTIONS} from "../PresentContinuouslyForm";

export type LayoutEditableField = {
  field: string;
  width: number;
  isDisabled?: boolean;
  hoverContent?: string | JSX.Element;
};

export interface FieldsLayoutEditorProps {
  fields: LayoutEditableField[];
  availableWidth: number;
  onChange: (fields: LayoutEditableField[]) => void;
}

const FieldEditorContainer = styled.div`
  background-color: #f3f4f6;
  padding: 16px;
  border-radius: 8px;
  font-size: 14px;
`;

const InfoBar = styled.div`
  margin-bottom: 8px;
  color: ${COLORS.TEXT_GRAY};
`;

const Track = styled.div`
  display: flex;
`;

const Header = styled.div`
  width: 180px;
  margin-right: -1px;

  & > .cell:first-of-type {
    border-top-left-radius: 6px;
  }
  & > .cell:last-of-type {
    border-bottom-left-radius: 6px;
  }
`;

const Body = styled.div`
  display: flex;

  & > .item:last-of-type {
    .cell:first-of-type {
      border-top-right-radius: 6px;
    }
    .cell:last-of-type {
      border-bottom-right-radius: 6px;
    }
  }
`;

const Item = styled.div``;

const Cell = styled.div`
  display: flex;
  align-items: center;
  width: 180px;
  height: 32px;
  padding: 8px;
  border: 1px solid ${COLORS.COLOR_BORDER};
  background-color: ${COLORS.WHITE};
  margin: -1px;
`;

const HeaderCell = styled(Cell)`
  background-color: transparent;
`;

const GrayText = styled(Text)`
  color: ${COLORS.TEXT_GRAY};
`;

const WidthInput = styled.input`
  width: 100%;
  height: 24px;
  padding: 4px;
  border: 1px solid ${COLORS.COLOR_BORDER};
  border-radius: 6px;
  text-align: center;
  appearance: textfield;
  -moz-appearance: textfield; /* Firefox */

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &:focus {
    border-color: #4f8bf9;
    outline: none;
  }
`;

const FieldsLayoutEditor = ({fields, availableWidth, onChange}: FieldsLayoutEditorProps) => {
  const handleDragEnd: OnDragEndResponder = (result) => {
    if (!result.destination) return;
    const items = Array.from(fields);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    onChange(items);
  };

  const handleWidthChange = (index: number, newWidth: number) => {
    if (newWidth < 0 || newWidth > availableWidth) return;
    const updatedFields = [...fields];
    updatedFields[index].width = newWidth;
    onChange(updatedFields);
  };

  const handleBlurOrEnter = (index: number) => {
    if (fields.length !== 2) return;
    const updatedFields = [...fields];
    const changedField = updatedFields[index];
    const otherFieldIndex = 1 - index;
    const otherField = updatedFields[otherFieldIndex];

    const newWidthForOther = availableWidth - changedField.width;
    otherField.width = newWidthForOther;

    onChange(updatedFields);
  };

  return (
    <FieldEditorContainer>
      <InfoBar>Space available: {availableWidth}px</InfoBar>
      <Track>
        <Header>
          <HeaderCell className="cell">
            <GrayText>Selected datapoints</GrayText>
          </HeaderCell>
          <HeaderCell className="cell">
            <GrayText>Space distribution (px)</GrayText>
          </HeaderCell>
        </Header>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="FieldsLayoutEditor" direction="horizontal">
            {(provided) => (
              <Body {...provided.droppableProps} ref={provided.innerRef}>
                {fields.map((field, index) => {
                  return (
                    <Item key={field.field} className="item" data-testid={`field-editor-item-${field.field}`}>
                      <Draggable key={field.field} draggableId={field.field} index={index}>
                        {(provided, snapshot) => {
                          return (
                            <Tooltip content={field.hoverContent} position="top" disabled={!field.hoverContent}>
                              <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                <Cell className="cell">
                                  <DragIcon isDragging={snapshot.isDragging} />
                                  <Text>
                                    {STOP_INFORMATION_OPTIONS.find((item) => item.value === field.field)?.label ??
                                      field.field}
                                  </Text>
                                </Cell>
                                <Cell className="cell">
                                  <WidthInput
                                    type="number"
                                    value={field.width}
                                    onChange={(e) => handleWidthChange(index, Number(e.target.value))}
                                    onBlur={() => handleBlurOrEnter(index)}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        handleBlurOrEnter(index);
                                      }
                                    }}
                                    disabled={field.isDisabled}
                                  />
                                </Cell>
                              </div>
                            </Tooltip>
                          );
                        }}
                      </Draggable>
                    </Item>
                  );
                })}
              </Body>
            )}
          </Droppable>
        </DragDropContext>
      </Track>
    </FieldEditorContainer>
  );
};

export default FieldsLayoutEditor;
